/**
 * Lazy-loaded season chart — imported via React.lazy in Dashboard.js.
 * Recharts (~300 kB) is only bundled into this chunk, which loads after the
 * rest of the Dashboard has painted.
 */
import React from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';

const CHART_COLORS = [
  'var(--brand-primary)', 'var(--brand-secondary)', '#ff9a1f', '#c084fc', '#f472b6',
  '#34d399', '#fb923c', '#60a5fa', '#fbbf24', '#a78bfa',
];

/**
 * Props:
 *   seasonData      – chart rows array
 *   seasonDrivers   – driver objects with driver_id
 *   chartEvents     – { [week]: events[] } for reference lines
 *   currentWeek     – used for X axis tick interval
 *   height          – ResponsiveContainer height (px)
 *   margin          – AreaChart margin object
 *   gradientPrefix  – unique prefix so mobile + desktop gradients don't collide ('mg'/'dg')
 *   strokeWidth     – Area stroke width (1.5 for mobile, 2 for desktop)
 *   tickFontSize    – font size for axis ticks
 *   tooltipEl       – pre-built <SeasonTooltip .../> element from parent
 */
export default function DashboardAreaChart({
  seasonData,
  seasonDrivers,
  chartEvents,
  currentWeek,
  height = 260,
  margin = { top: 8, right: 16, left: 0, bottom: 0 },
  gradientPrefix = 'g',
  strokeWidth = 2,
  tickFontSize = 10,
  tooltipEl,
}) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={seasonData} margin={margin}>
        <defs>
          {seasonDrivers.map((d, i) => {
            const color = CHART_COLORS[i % CHART_COLORS.length];
            return (
              <linearGradient key={d.driver_id} id={`${gradientPrefix}${d.driver_id}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor={color} stopOpacity={0.45} />
                <stop offset="95%" stopColor={color} stopOpacity={0.05} />
              </linearGradient>
            );
          })}
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(59,73,75,0.2)" vertical={false} />
        <XAxis
          dataKey="week"
          tickFormatter={w => `KW${w}`}
          interval={Math.max(0, Math.ceil(currentWeek / 7) - 1)}
          tick={{ fontFamily: "'JetBrains Mono', monospace", fontSize: tickFontSize, fill: '#849495' }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          tick={{ fontFamily: "'JetBrains Mono', monospace", fontSize: tickFontSize, fill: '#849495' }}
          axisLine={false}
          tickLine={false}
        />
        <Tooltip content={tooltipEl} />
        {seasonDrivers.map((d, i) => {
          const color = CHART_COLORS[i % CHART_COLORS.length];
          return (
            <Area
              key={d.driver_id}
              type="monotone"
              dataKey={String(d.driver_id)}
              stackId="1"
              stroke={color}
              strokeWidth={strokeWidth}
              fill={`url(#${gradientPrefix}${d.driver_id})`}
            />
          );
        })}
        {Object.entries(chartEvents).map(([w, evs]) => (
          <ReferenceLine
            key={w}
            x={Number(w)}
            stroke="rgba(var(--brand-secondary-rgb),0.45)"
            strokeDasharray="3 3"
            label={{ value: '◆', position: 'insideTopLeft', fontSize: tickFontSize - 2, fill: 'rgba(var(--brand-secondary-rgb),0.7)' }}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
}

export { CHART_COLORS };
